import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { getValidAuthHeadersOrThrow } from '../../lib/auth';
import { getAdminId } from '../../utils/getAdminId';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Mail, Eye, EyeOff, CircleCheck as CheckCircle, Circle as XCircle, Loader as Loader2, Save, Wifi } from 'lucide-react';

interface BrevoConfig {
  api_key: string;
  sender_name: string;
  sender_email: string;
}

export const BrevoSettingsTab: React.FC = () => {
  const { user } = useAuth();
  const [formData, setFormData] = useState<BrevoConfig>({
    api_key: '',
    sender_name: '',
    sender_email: '',
  });
  const [isConfigured, setIsConfigured] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [loaded, setLoaded] = useState(false);

  const adminId = getAdminId(user);

  useEffect(() => {
    const load = async () => {
      if (!adminId) return;

      const { data } = await supabase
        .from('admin_brevo_credentials')
        .select('api_key, sender_name, sender_email')
        .eq('admin_id', adminId)
        .maybeSingle();

      if (data) {
        setFormData({
          api_key: data.api_key,
          sender_name: data.sender_name,
          sender_email: data.sender_email,
        });
        setIsConfigured(true);
      }
      setLoaded(true);
    };

    load();
  }, [adminId]);

  const handleSave = async () => {
    if (!formData.api_key.trim()) {
      alert('API Key is required');
      return;
    }
    if (!formData.sender_email.trim()) {
      alert('Sender email is required');
      return;
    }
    if (!adminId) {
      alert('No active company selected');
      return;
    }

    setSaving(true);
    try {
      const { error } = await supabase
        .from('admin_brevo_credentials')
        .upsert({
          admin_id: adminId,
          api_key: formData.api_key.trim(),
          sender_name: formData.sender_name.trim(),
          sender_email: formData.sender_email.trim(),
          configured_by: user!.id,
          configured_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          is_active: true,
        }, { onConflict: 'admin_id' });

      if (error) throw error;

      setIsConfigured(true);
      alert('Brevo credentials saved successfully');
    } catch (err) {
      console.error('Error saving Brevo credentials:', err);
      alert('Failed to save credentials');
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    if (!adminId) {
      setTestResult({ success: false, message: 'No active company selected' });
      return;
    }
    setTesting(true);
    setTestResult(null);

    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-brevo-email`,
        {
          method: 'POST',
          headers: await getValidAuthHeadersOrThrow(),
          body: JSON.stringify({ admin_id: adminId, action: 'test_connection' }),
        }
      );

      const result = await response.json();
      setTestResult(result);
    } catch (err) {
      setTestResult({ success: false, message: 'Connection test failed' });
    } finally {
      setTesting(false);
      setTimeout(() => setTestResult(null), 8000);
    }
  };

  if (!loaded) {
    return (
      <div className="card p-6 flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="card p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center">
          <Mail className="h-5 w-5 text-blue-600 dark:text-blue-400" />
        </div>
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            Brevo Email Configuration
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Configure email delivery for sales history pick list notifications
          </p>
        </div>
        {isConfigured && (
          <span className="ml-auto inline-flex items-center gap-1.5 px-3 py-1 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 rounded-full text-xs font-semibold">
            <CheckCircle className="h-3.5 w-3.5" />
            Configured
          </span>
        )}
      </div>

      <div className="space-y-5">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
            API Key
          </label>
          <div className="relative">
            <input
              type={showApiKey ? 'text' : 'password'}
              value={formData.api_key}
              onChange={e => setFormData(prev => ({ ...prev, api_key: e.target.value }))}
              placeholder="xkeysib-..."
              className="w-full px-4 py-2.5 pr-12 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono"
            />
            <button
              onClick={() => setShowApiKey(!showApiKey)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              {showApiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Your Brevo API key from Settings &gt; SMTP & API &gt; API Keys
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              Sender Name
            </label>
            <input
              type="text"
              value={formData.sender_name}
              onChange={e => setFormData(prev => ({ ...prev, sender_name: e.target.value }))}
              placeholder="My Store"
              className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              Sender Email
            </label>
            <input
              type="email"
              value={formData.sender_email}
              onChange={e => setFormData(prev => ({ ...prev, sender_email: e.target.value }))}
              placeholder="noreply@mystore.com"
              className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        {testResult && (
          <div className={`flex items-center gap-2 p-4 rounded-xl text-sm font-medium ${
            testResult.success
              ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-800'
              : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800'
          }`}>
            {testResult.success ? (
              <CheckCircle className="h-5 w-5 flex-shrink-0" />
            ) : (
              <XCircle className="h-5 w-5 flex-shrink-0" />
            )}
            {testResult.success ? `Connected: ${testResult.message || 'API key verified'}` : testResult.message}
          </div>
        )}

        <div className="flex items-center gap-3 pt-2">
          <Button onClick={handleSave} variant="primary" loading={saving}>
            <Save className="h-4 w-4 mr-1.5" />
            Save Credentials
          </Button>
          <Button
            onClick={handleTestConnection}
            variant="secondary"
            loading={testing}
            disabled={!formData.api_key.trim()}
          >
            <Wifi className="h-4 w-4 mr-1.5" />
            Test Connection
          </Button>
        </div>
      </div>
    </div>
  );
};
